/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ============================================================
   Rubberbotenwinkel.nl — winkelwagen-icoon direct bijwerken
   Toegevoegd op verzoek van Emre (sept 2026).
   Zorgt ervoor dat het getal op het winkelmandje-icoon in de
   header direct wordt bijgewerkt zodra een product wordt
   toegevoegd of gewijzigd, zonder dat de pagina ververst hoeft
   te worden. Gebruikt het officiële "cart:updated" event dat
   het Focal-thema zelf al uitstuurt (zie documentatie hierboven).

   Fix (25-09-2026, na feedback Don Kemper): het getal bleef alsnog
   onzichtbaar na het toevoegen van een product. De opmaak
   (assets/custom-mobile-fixes.css, .header__cart-count--floating)
   en deze listener zijn zelf logisch correct, maar konden niet
   volledig worden geverifieerd omdat het thema's eigen, ingebouwde
   cart-count-broncode in een groot, geminificeerd kernbestand
   (assets/theme.js, 272 KB) zit dat niet doorzoekbaar was vanuit
   deze omgeving. Meest waarschijnlijke oorzaak: als het thema zelf
   ook naar hetzelfde "cart:updated"-event luistert en het
   <cart-count>-element in dezelfde tik opnieuw rendert, kan die
   ingebouwde afhandeling onze klasse-wijziging ongedaan maken al
   naar gelang de volgorde waarin de listeners worden uitgevoerd.
   Verdedigende fix: de eigen update nu welbewust een frame later
   toepassen (dubbele requestAnimationFrame, hetzelfde patroon als
   al eerder gebruikt in snippets/desktop-menu-premium.liquid), zodat
   onze wijziging altijd als laatste — en dus zichtbaar — wordt
   toegepast, ongeacht wat het thema zelf in dezelfde tik doet.
   ============================================================ */
document.addEventListener('cart:updated', function (event) {
  var cart = event.detail && event.detail.cart;

  if (!cart) {
    return;
  }

  function applyCartCount() {
    document.querySelectorAll('cart-count').forEach(function (element) {
      element.textContent = cart.item_count;

      if (cart.item_count > 0) {
        element.classList.add('header__cart-count--has-items');
      } else {
        element.classList.remove('header__cart-count--has-items');
      }
    });
  }

  requestAnimationFrame(function () {
    requestAnimationFrame(applyCartCount);
  });
});

/* ============================================================
   Rubberbotenwinkel.nl — mobiel hoofdmenu redesign (sept 2026)
   Toegevoegd op verzoek van Emre.
   Zet de klasse "mobile-menu-is-open" op <html> zolang de
   schermvullende mobiele menu-drawer (id "mobile-menu-drawer")
   openstaat. Dit gebeurt door te luisteren naar het officiële
   "open"-attribuut dat het Focal-thema zelf al op elk
   drawer-element zet/verwijdert (zie developer-documentatie
   hierboven) — er wordt niets veranderd aan hoe de drawer zelf
   opent/sluit, focus vasthoudt of reageert op de Escape-toets;
   dat blijft volledig de eigen afhandeling van het thema.
   De klasse wordt alleen gebruikt om (1) de achtergrondpagina
   te blokkeren voor scrollen en (2) de zwevende WhatsApp-knop
   tijdelijk te verbergen (zie assets/custom-mobile-fixes.css).
   ============================================================ */
(function () {
  var mobileMenu = document.getElementById('mobile-menu-drawer');

  if (!mobileMenu) {
    return;
  }

  var syncMobileMenuState = function () {
    document.documentElement.classList.toggle('mobile-menu-is-open', mobileMenu.hasAttribute('open'));
  };

  syncMobileMenuState();

  new MutationObserver(syncMobileMenuState).observe(mobileMenu, {
    attributes: true,
    attributeFilter: ['open']
  });
})();
